"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { destinationById } from "@/shared/config/app-destinations";
import { getEvent, useAnalytics } from "@/lib/analytics";
import { useDismissSpotlightCoach, useSpotlightCoach } from "../model/use-spotlight-coach";
import { SpotlightCoachMark } from "./spotlight-coach-mark";

/**
 * On landing with `?spotlight=<postId>`: the coach mark for the destination
 * that spotlight opened, but only when the current path IS that destination's
 * path. "Got it" dismisses it on the server, then strips the parameter.
 */
export function SpotlightLandingCoach() {
  const $ = _c(29);
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams?.get("spotlight") ?? null;
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const postId = t0;
  const coach = useSpotlightCoach(postId);
  const dismiss = useDismissSpotlightCoach();
  const {
    track
  } = useAnalytics();
  let t1;
  if ($[2] !== coach) {
    t1 = coach ? destinationById(coach.destinationId) : null;
    $[2] = coach;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const destination = t1;
  if (postId === null || coach === null || destination === null || destination.path !== pathname) {
    return null;
  }
  let t2;
  if ($[4] !== coach.destinationId || $[5] !== coach.version || $[6] !== postId || $[7] !== track) {
    t2 = anchored => track(getEvent("feed-spotlight", "spotlight_coach_mark_shown"), {
      postId,
      version: coach.version,
      destinationId: coach.destinationId,
      anchored
    });
    $[4] = coach.destinationId;
    $[5] = coach.version;
    $[6] = postId;
    $[7] = track;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const trackShown = t2;
  let t3;
  if ($[9] !== coach.destinationId || $[10] !== coach.version || $[11] !== postId || $[12] !== track) {
    t3 = () => track(getEvent("feed-spotlight", "spotlight_coach_mark_target_missing"), {
      postId,
      version: coach.version,
      destinationId: coach.destinationId
    });
    $[9] = coach.destinationId;
    $[10] = coach.version;
    $[11] = postId;
    $[12] = track;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const trackMissing = t3;
  let t4;
  if ($[14] !== coach.destinationId || $[15] !== coach.version || $[16] !== dismiss || $[17] !== pathname || $[18] !== postId || $[19] !== router || $[20] !== searchParams || $[21] !== track) {
    t4 = () => {
      track(getEvent("feed-spotlight", "spotlight_coach_mark_dismissed"), {
        postId,
        version: coach.version,
        destinationId: coach.destinationId
      });
      dismiss.mutate({
        postId,
        version: coach.version
      });
      const params = new URLSearchParams(searchParams?.toString() ?? "");
      params.delete("spotlight");
      const query = params.toString();
      router.replace(query ? `${pathname}?${query}` : pathname, {
        scroll: false
      });
    };
    $[14] = coach.destinationId;
    $[15] = coach.version;
    $[16] = dismiss;
    $[17] = pathname;
    $[18] = postId;
    $[19] = router;
    $[20] = searchParams;
    $[21] = track;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  const onDismiss = t4;
  let t5;
  if ($[23] !== destination.coach.targetId || $[24] !== destination.coach.text || $[25] !== onDismiss || $[26] !== trackMissing || $[27] !== trackShown) {
    t5 = <SpotlightCoachMark targetId={destination.coach.targetId} text={destination.coach.text} onDismiss={onDismiss} onShown={trackShown} onTargetMissing={trackMissing} />;
    $[23] = destination.coach.targetId;
    $[24] = destination.coach.text;
    $[25] = onDismiss;
    $[26] = trackMissing;
    $[27] = trackShown;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  return t5;
}